import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { CustomerLocationDto, DateRange } from '../types';
import { sanitizeLocationDates } from '@/lib/locationDates';

export type CustomerType = 'traveler' | 'local';
export type DateMode = 'fixed' | 'flexible';
export type RentalMode = 'all' | 'short-term' | 'subscription';

/**
 * Selected tier when user is in flexible dates mode
 */
export interface SelectedTier {
    tierName: string;
    minDays: number;
    maxDays: number;
    price: number;
}

/**
 * Location Store
 *
 * Manages location and date selection state for the rental flow.
 * Note: Fulfillment is pickup-only (no delivery support in MVP).
 * See: docs/customer-website/spec/spec-design-pickup-only-fulfillment.md
 */
interface LocationState {
    location: CustomerLocationDto | null;
    dateRange: DateRange;
    customerType: CustomerType;
    dateMode: DateMode;
    rentalMode: RentalMode;
    selectedTier: SelectedTier | null;
    pickupDate: Date | null;

    setLocation: (location: CustomerLocationDto | null) => void;
    setDateRange: (range: DateRange) => void;
    setCustomerType: (type: CustomerType) => void;
    setDateMode: (mode: DateMode) => void;
    setRentalMode: (mode: RentalMode) => void;
    setSelectedTier: (tier: SelectedTier | null) => void;
    setPickupDate: (date: Date | null) => void;
    clearAfterCheckout: () => void;
    reset: () => void;
}

export const useLocationStore = create<LocationState>()(
    persist(
        (set) => ({
            location: null,
            dateRange: { from: undefined, to: undefined },
            customerType: 'traveler',
            dateMode: 'fixed',
            rentalMode: 'all',
            selectedTier: null,
            pickupDate: null,

            setLocation: (location) => set({ location }),
            setDateRange: (dateRange) => set({ dateRange }),
            setCustomerType: (customerType) => set({ customerType }),
            setDateMode: (dateMode) => set({
                dateMode,
                // Clear dates when switching to flexible mode
                ...(dateMode === 'flexible' ? { dateRange: { from: undefined, to: undefined } } : {}),
                // Clear tier when switching to fixed mode
                ...(dateMode === 'fixed' ? { selectedTier: null, pickupDate: null } : {})
            }),
            setRentalMode: (rentalMode) => set((state) => ({
                rentalMode,
                // Subscription mode: force flexible dates (subscriptions don't use specific rental dates)
                ...(rentalMode === 'subscription' ? {
                    dateMode: 'flexible' as DateMode,
                    dateRange: { from: undefined, to: undefined },
                    selectedTier: null,
                } : {}),
                // Short-term mode: restore fixed dates if we were in subscription mode
                ...(rentalMode === 'short-term' && state.rentalMode === 'subscription' ? {
                    dateMode: 'fixed' as DateMode,
                } : {}),
            })),
            setSelectedTier: (selectedTier) => set({ selectedTier }),
            setPickupDate: (pickupDate) => set({ pickupDate }),
            clearAfterCheckout: () => set({
                location: null,
                dateRange: { from: undefined, to: undefined },
                selectedTier: null,
                pickupDate: null,
            }),
            reset: () => set({
                location: null,
                dateRange: { from: undefined, to: undefined },
                customerType: 'traveler',
                dateMode: 'fixed',
                rentalMode: 'all',
                selectedTier: null,
                pickupDate: null,
            }),
        }),
        {
            name: 'stork-location-storage',
            storage: createJSONStorage(() => localStorage, {
                reviver: (key, value) => {
                    if (value && typeof value === 'string' && (key === 'from' || key === 'to' || key === 'pickupDate')) {
                        return new Date(value);
                    }
                    return value;
                },
            }),
            onRehydrateStorage: () => (state) => {
                if (!state) return;

                const sanitized = sanitizeLocationDates(state.dateRange, state.pickupDate);
                if (sanitized.dateRangeCleared) {
                    state.setDateRange(sanitized.dateRange);
                }
                if (sanitized.pickupDateCleared) {
                    state.setPickupDate(sanitized.pickupDate);
                }
            },
        }
    )
);

// Selectors
/**
 * Get the URL path for the current location (slug-based)
 * Returns slug if available, falls back to ID
 */
export const useLocationPath = () => {
    const location = useLocationStore((state) => state.location);
    if (!location) return null;
    return location.slug || location.id;
};

/**
 * Get the full URL path for the current location
 */
export const useLocationUrl = () => {
    const path = useLocationPath();
    if (!path) return null;
    return `/locations/${path}`;
};
